import { AccountsClient } from '../../../clients/stratus/accountmgtsvc';
import { getServices } from '../../../infra/startup';
import { IAuthProviderService } from '../../authProviderService';
import * as T from './types';
import { AuthContextEnum } from '../../authTokenService';
import { ISessionService } from '../../session';
import TenantUtils from '../TenantUtils';
import TenantFilterBuilder from '../tenantFilterBuilder/TenantFilterBuilder';
import StratusTenantHandler from './StratusTenantHandler';
import TenantStrategy from './TenantStrategy';
import { TenantStrategyEnum } from './strategy';
import UserTenantDetailsClient from '../../../clients/stratus/usersMgt/UserTenantDetailsClient';
import UserMgtClient from '../../../clients/stratus/usersMgt/UserMgtClient';
import { ResourceListType } from '../../../clients/stratus/tntCommonResourceRegistry/types';
import { DEFAULT_COUNTRY, DEFAULT_LANGUAGE } from './constants';
import { internalLogger } from '../../../interface/v1/clientOsLogger';

type OrganizationProperties = {
  fallback?: string;
  filter?: {
    role: 'admin' | 'user' | 'all';
    tenantType: string[];
  };
};

export default class AstBasedTenantHandler
  extends StratusTenantHandler
  implements TenantStrategy
{
  private _strategy = TenantStrategyEnum.stratusOrganization;
  private _options: OrganizationProperties;
  private _assetReference: string;
  private _accountsClient: AccountsClient;
  private _sessionService: ISessionService;
  private _userTenantDetailsClient: UserTenantDetailsClient;
  private _userMgtClient: UserMgtClient;

  constructor({
    stack,
    options,
    assetReference,
    sessionService
  }: T.TenantHandlerStrategyType) {
    super({
      stack,
      authProvider: getServices().authProviderService,
      parentAuthProvider: getServices().authProviderService
    });
    this._options = options;
    this._assetReference = assetReference;
    this._sessionService = sessionService;
    this._accountsClient = new AccountsClient(
      AccountsClient.getBaseUrl(this._stack),
      this._authProvider
    );
    this._userTenantDetailsClient = new UserTenantDetailsClient(
      UserTenantDetailsClient.getBaseUrl(this._stack),
      this._parentAuthProvider
    );
    this._userMgtClient = new UserMgtClient(
      UserMgtClient.getBaseUrl(stack),
      this._authProvider
    );
  }

  public getAssetReference(): string {
    return this._assetReference;
  }

  public getStrategy = (): TenantStrategyEnum => {
    return this._strategy;
  };

  public setTenant = async (
    id: string,
    authContext: AuthContextEnum
  ): Promise<void> => {
    // TODO Check if we can use loggerService here
    console.warn(
      `Method setTenant is not implemented - tenantId: ${id} - authContext: ${authContext}`
    );
    return;
  };

  public async createTenant(
    options: T.CreateTenantStrategyOptionsType
  ): Promise<T.CreateTenantResultType> {
    throw new Error('Method not supported for AST.');
  }

  /**
   * Fetches paginated tenant list data from the backend services
   * @param options - Options for pagination and filtering
   * @returns Promise resolving to paginated tenant visualization data
   */
  public async _getPaginatedTenantListData(
    options: T.GetTenantListOptionsType
  ): Promise<T.PaginatedTenantVisualizationType> {
    const { filter } = this._options || {};
    const userResourceId = await this._userMgtClient
      .getUsersMe()
      .then((response) => response.resourceId);

    const _options = {
      userResourceId,
      ...options
    };

    const userTenantDetails =
      await this._userTenantDetailsClient.getUserTenantDetails(_options);

    const tenantList = await Promise.all<T.TenantNodeType>(
      userTenantDetails?.resourceList?.map(
        async (resource: ResourceListType) => {
          const tenant = await this._nodeClient.getNodeByTenantId({
            tenantId: resource.tenantResourceId
          });
          return {
            ...tenant,
            roleCategory: resource.roleCategory,
            accountName: `AST-${tenant?.accountName}`
          };
        }
      )
    );

    const tenants = tenantList.map((filteredTenant) =>
      TenantUtils._convertStratusTenantNodeToTenantVisualizationType(
        filteredTenant
      )
    );

    const filteredTenantList = this.getFilteredTenantList(filter, tenants);

    return {
      totalPages: 0,
      currentPage: 1,
      tenants: filteredTenantList
    };
  }

  private getFilteredTenantList(
    filter: T.TenantHandlerFilterType,
    tenants: T.TenantVisualizationType[]
  ) {
    return new TenantFilterBuilder({
      filter,
      tenants
    })
      .filterByRegexTenantType()
      .getTenantList();
  }

  public getAuthProvider(): IAuthProviderService {
    return this._authProvider;
  }

  public isRequiredToNavigate = (): boolean => {
    return true;
  };

  public getFallback = (): string => {
    return this?._options?.fallback;
  };

  isTenantValid(
    filter: T.TenantHandlerFilterType,
    tenant: T.TenantVisualizationType
  ): boolean {
    return this.getFilteredTenantList(filter, [tenant])?.length > 0;
  }
}
